import { useMemo } from "react";
import { Group, Select, Stack, Text } from "@mantine/core";
import { Alert } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { ModelUsageRow } from "@models/adminMonitoring";
import type { Deprecation } from "@models/providerCatalog";
import { adminMonitoringApi } from "@services/adminMonitoringApi";
import { useApiResource } from "@hooks/monitoring/useApiResource";
import { useMonitoringFilters } from "@hooks/monitoring/useMonitoringFilters";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { WindowToolbar } from "@features/Insights";
import { DeprecationBadge } from "@features/Providers";
import { AdminTable, col } from "@components/AdminTable";
import { moduleLabel } from "@utils/monitoringFormat";

interface ModelUsageTabProps {
  labels: Record<string, string>;
  deprecationOf: (providerKey: string, modelId: string) => Deprecation | null;
}

/**
 * Which module and service called which model in the window, from metering (what
 * actually ran, not what is configured). Deprecated models carry their badge, so
 * the modules that must move are visible next to the date.
 */
const ModelUsageTab = ({ labels, deprecationOf }: ModelUsageTabProps) => {
  const { t } = useTranslation();
  const { canViewAllOrganisations } = useAdminPermissions();
  const monitoring = useMonitoringFilters();
  const { filters, values } = monitoring;
  const scoped = { from: filters.from, to: filters.to, organisationId: values.org };
  const usage = useApiResource(`model-usage:${JSON.stringify(scoped)}`, (signal) =>
    adminMonitoringApi().modelUsage(scoped, signal));
  const organisations = useApiResource(canViewAllOrganisations ? `orgs:${filters.from}:${filters.to}` : null, (signal) =>
    adminMonitoringApi().facets({ from: filters.from, to: filters.to }, signal));

  const moduleFilter = monitoring.raw("module");
  const modules = useMemo(() => [...new Set((usage.data?.rows ?? []).map((r) => r.module_name))].sort(), [usage.data]);
  const rows = (usage.data?.rows ?? []).filter((r) => !moduleFilter || r.module_name === moduleFilter);

  const columns = [
    col.twoLine<ModelUsageRow>("model", t("admin.models.columns.model"), (r) => ({
      primary: r.model_id, secondary: labels[r.provider_key] ?? r.provider_key,
    }), { mono: true }),
    col.badge<ModelUsageRow>("status", t("admin.models.columns.status"), (r) => {
      const d = deprecationOf(r.provider_key, r.model_id);
      return d ? <DeprecationBadge deprecation={d} /> : <Text size="sm" c="dimmed">-</Text>;
    }, 176),
    col.twoLine<ModelUsageRow>("module", t("admin.insights.columns.moduleService"), (r) => ({
      primary: moduleLabel(r.module_name), secondary: r.service_name,
    }), { width: "18%" }),
    col.number<ModelUsageRow>("events", t("admin.insights.columns.events"), (r) => r.event_count),
    col.number<ModelUsageRow>("orgs", t("admin.models.usage.organisations"), (r) => r.organisations_count),
    col.date<ModelUsageRow>("last", t("admin.insights.columns.lastUsed"), (r) => r.last_used),
    col.money<ModelUsageRow>("cost", t("admin.insights.columns.cost"), (r) => ({
      amount: r.costs[0]?.total_cost ?? null,
      currency: r.costs[0]?.currency ?? "USD",
      note: r.unpriced_count ? t("admin.insights.unpricedBadge", { count: r.unpriced_count }) : undefined,
      warn: r.unpriced_count > 0,
    })),
  ];

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <Select
            size="sm"
            w={220}
            clearable
            placeholder={t("admin.insights.filters.allModules")}
            data={modules.map((m) => ({ value: m, label: moduleLabel(m) }))}
            value={moduleFilter}
            onChange={(value) => monitoring.update({ module: value })}
            aria-label={t("admin.insights.filters.module")}
          />
          <Text size="sm" c="dimmed">{t("admin.models.usage.count", { count: rows.length })}</Text>
        </Group>
        <WindowToolbar
          preset={monitoring.preset}
          custom={monitoring.custom}
          onPresetChange={monitoring.setPreset}
          onRefresh={() => { monitoring.refresh(); usage.reload(); }}
          loading={usage.loading}
          organisations={canViewAllOrganisations ? organisations.data?.organisations ?? [] : undefined}
          organisationId={values.org}
          onOrganisationChange={(value) => monitoring.setFilter("org", value)}
        />
      </Group>
      {usage.error && <Alert color={usage.forbidden ? "gray" : "red"}>{usage.error}</Alert>}
      <AdminTable
        columns={columns}
        data={rows}
        getRowKey={(r) => `${r.provider_key}|${r.model_id}|${r.module_name}|${r.service_name}`}
        loading={usage.loading}
        emptyMessage={t("admin.models.usage.empty")}
      />
      <Text size="xs" c="dimmed">{t("admin.models.usage.footnote")}</Text>
    </Stack>
  );
};

export default ModelUsageTab;
